import {
  Component,
  ElementRef,
  HostListener,
  inject,
  ViewChild,
} from '@angular/core';
import { FieldType, FieldTypeConfig, FormlyFieldProps } from '@ngx-formly/core';
import { FormlyFieldSelectProps } from '@ngx-formly/core/select';
import { MultiSelect } from 'primeng/multiselect';
import { FORM_IMPORTS } from '../../_shared/_imports/form.import';

import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { DrawerModule } from 'primeng/drawer';
import { CommonModule } from '@angular/common';
import { TableModule } from 'primeng/table';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AutoFocusModule } from 'primeng/autofocus';

export interface MultiSelectFieldProps
  extends FormlyFieldProps,
    FormlyFieldSelectProps {
  appendTo?: any;
  filter?: boolean;
  optionLabel?: string;
  optionValue?: string;
  group?: boolean;
  optionGroupLabel?: string;
  optionGroupChildren?: string;
  showClear?: boolean;
  nestedValue?: boolean;
  optionDescription?: string;
  filterBy?: string;
  showToggleAll?: boolean;
  translateLabel?: string;
}

@Component({
  selector: 'multiselect-field',
  standalone: true,
  imports: [
    CommonModule,
    ...FORM_IMPORTS,
    MultiSelect,
    IconFieldModule,
    InputIconModule,
    InputText,
    DrawerModule,
    TableModule,
    TranslatePipe,
    AutoFocusModule,
  ],
  template: `
    <p-multiSelect
      #dropdown
      (onClick)="handleDropdownClick()"
      dropdownIcon="fa-solid fa-caret-down text-xl"
      class="w-full"
      [formControl]="formControl"
      [formlyAttributes]="field"
      [options]="$any(props.options)"
      autocomplete="false"
      [placeholder]="props.placeholder"
      [virtualScroll]="true"
      [filter]="props.filter"
      [virtualScrollItemSize]="43"
      [optionLabel]="props.optionLabel ?? 'label'"
      [showToggleAll]="props.showToggleAll ?? true"
      [optionValue]="
        props.nestedValue ? undefined : (props.optionValue ?? 'value')
      "
      [group]="props.group"
      [optionGroupLabel]="props.optionGroupLabel ?? 'label'"
      [optionGroupChildren]="props.optionGroupChildren ?? 'items'"
      [filterBy]="props.filterBy ?? undefined"
    >
      <ng-template #selecteditems let-selected>
        {{ getLabel(selected) }}
      </ng-template>

      <ng-template let-el #item>
        <div>
          @if (props.translateLabel) {
            {{
              props.translateLabel +
                '.' +
                (props.optionValue ? el[props.optionValue] : el) | translate
            }}
          } @else {
            {{ el[props.optionLabel ?? 'label'] }}
          }

          @if (props.optionDescription) {
            <div class="text-xs italic">
              {{ el[props.optionDescription] }}
            </div>
          }
        </div>
      </ng-template>
    </p-multiSelect>

    <p-drawer
      [(visible)]="drawer"
      [position]="'bottom'"
      styleClass="h-auto min-h-[655px] pt-22"
      baseZIndex="1104"
      [maskStyle]="$any('z-index:1103')"
      (onShow)="onShow($event)"
    >
      <ng-template #closeicon>
        <i class="fa-solid fa-caret-up text-xl"></i>
      </ng-template>

      <ng-template #header>
        <div class="flex items-center gap-2">
          <span class="font-bold">{{
            props.label != '' ? props.label : props.placeholder
          }}</span>
        </div>
      </ng-template>

      <p-table
        #dt2
        scrollHeight="500px"
        [virtualScroll]="true"
        [virtualScrollItemSize]="46"
        [value]="$any(props.options)"
        selectionMode="multiple"
        [(selection)]="selected"
        [globalFilterFields]="
          $any(props.filterBy ?? props.optionLabel ?? 'label').split(',')
        "
        (selectionChange)="selectionChange($event)"
      >
        @if (props.filter) {
          <ng-template #caption>
            <div class="flex">
              <p-iconfield iconPosition="left" class="w-full">
                <input
                  #search
                  pInputText
                  [pAutoFocus]="true"
                  type="text"
                  (input)="
                    dt2.filterGlobal($any($event.target).value, 'contains')
                  "
                /><p-inputicon>
                  <i class="fas fa-search"></i>
                </p-inputicon>
              </p-iconfield>
            </div>
          </ng-template>
        }
        <ng-template #body let-el>
          <tr [pSelectableRow]="el">
            <td class="w-[20px]">
              <p-tableCheckbox [value]="el" />
            </td>
            <td>
              <div>
                @if (props.translateLabel) {
                  {{
                    props.translateLabel +
                      '.' +
                      (props.optionValue ? el[props.optionValue] : el)
                      | translate
                  }}
                } @else {
                  {{ el[props.optionLabel ? props.optionLabel : 'label'] }}
                }
              </div>
              @if (props.optionDescription) {
                <div class="text-xs italic">
                  {{ el[props.optionDescription] }}
                </div>
              }
            </td>
          </tr>
        </ng-template>

        <ng-template #emptyMessage>
          {{ 'primeng.emptyMessage' | translate }}
        </ng-template>
      </p-table>
    </p-drawer>
  `,
  styles: `
    :host ::ng-deep {
      p-multiselect {
        @apply justify-between;
        @apply flex-wrap;
      }
      p-overlay {
        @apply w-full;
      }
      .p-multiselect-label-container {
        @apply flex-none;
        width: calc(100% - var(--p-multiselect-dropdown-width));
      }
      .p-multiselect-label {
        @apply block;
      }
    }
  `,
})
export class MultiSelectFieldType extends FieldType<
  FieldTypeConfig<MultiSelectFieldProps>
> {
  isMobile: boolean = false;
  @ViewChild('dt2') dt2!: any;
  @ViewChild('search', { static: false }) search?: ElementRef<HTMLInputElement>;

  private readonly _translate = inject(TranslateService);

  ngOnInit() {
    this.checkIfMobile();
  }

  @HostListener('window:resize', [])
  onResize() {
    this.checkIfMobile();
  }

  private checkIfMobile() {
    this.isMobile = window.innerWidth <= 768;
  }
  drawer: boolean = false;

  selected: any;

  handleDropdownClick() {
    if (this.isMobile) {
      this.drawer = !this.drawer;
    }
  }

  getLabel(s: any[]) {
    const seleteds = s && s.length > 0 ? s : this.formControl.value;

    let strings = seleteds?.map((selected: any) => {
      if (this.props.translateLabel) {
        return this._translate.instant(
          this.props.translateLabel +
            '.' +
            (this.props.optionValue
              ? selected[this.props.optionValue]
              : selected),
        );
      } else {
        return selected[this.props.optionLabel ?? 'label'];
      }
    });
    return (strings ?? []).join(', ');
  }

  selectionChange(event: any) {
    if (event) {
      if (this.props.optionValue && !this.props.nestedValue) {
        this.formControl.setValue(
          event.map((e: any) =>
            this.props.optionValue ? e[this.props.optionValue] : e,
          ),
        );
      } else {
        this.formControl.setValue(event);
      }
    } else {
      if (this.props.showClear) this.formControl.setValue(undefined);
    }

    // this.drawer = false;
  }

  onShow(event: any) {
    const options = this.props.options as any[];

    if (this.formControl.value) {
      // Se `formControl.value` è un array di ID
      const selectedValues = Array.isArray(this.formControl.value)
        ? this.formControl.value
        : [this.formControl.value];

      this.selected = options.filter((el) => {
        return selectedValues.includes(
          this.props.optionValue ? el[this.props.optionValue] : el,
        );
      });
      const firstIndex = options.findIndex((el) =>
        this.props.optionValue
          ? el[this.props.optionValue] == selectedValues[0]
          : el == selectedValues[0],
      );

      // Scrolla al primo selezionato
      setTimeout(() => {
        this.search?.nativeElement.focus();
        if (
          this.dt2?.scroller &&
          typeof this.dt2.scroller.scrollToIndex === 'function'
        ) {
          this.dt2.scroller.scrollToIndex(firstIndex);
        } else {
          console.warn('Scroller non disponibile o scrollToIndex non presente');
        }
      }, 0);
    }
  }
}
